import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import {
    clefs,
    keys,
    scales,
    solfaNameOptions,
    ABCOptions,
    intervalOptions,
    lengthenSolfa,
} from "../../../../common/parameters";

import { IStateTree } from "../../../store/interfaces";
import { IUser } from "../../../store/user/models";
import { toggleFilterCategory } from "../../../store/browse/actions";

import Icon from "../../../icon";
import { Select, SelectCheckbox, Label, Checkbox, TwoInOne } from "../../../components/Form";
import { useUser } from "../../../store/user/selectors";

import getFiltersValue from "../getFiltersValue";
import { Slider, Arrow } from "../commonStyles";

import messages from "./messages";
import {
    solfaOptions,
    ABCOptionsRange,
} from "./utils";
import { IProps } from "./interfaces";

const Pitch = ({ filters, change }: IProps) => {
    const dispatch = useDispatch();
    const { formatMessage, locale } = useIntl()
    const { solfaShort = false, solfaKam = false } = useUser()
    const isOpen = useSelector((state: IStateTree) => state.browse.filterCategories.pitch);
    const handleToggleCategory = () => {
        dispatch(toggleFilterCategory("pitch"));
    }
    return (
        <div>
            <h3 onClick={handleToggleCategory}>
                <FormattedMessage {...messages.pitch} />
                <Arrow opened={isOpen}>
                    <Icon i="chevron-right" />
                </Arrow>
            </h3>
            {isOpen && (
                <Slider>
                    <div>
                        <TwoInOne>
                            <Select
                                lightWithValue
                                label={formatMessage(messages.clef)}
                                name="pitch.clef"
                                value={getFiltersValue(filters, "pitch.clef") as string}
                                onChange={change}
                                options={clefs(formatMessage)}
                            />
                            <div className="Gap" />
                            <Select
                                lightWithValue
                                label={formatMessage(messages.key)}
                                name="pitch.key"
                                value={getFiltersValue(filters, "pitch.key") as string}
                                onChange={change}
                                options={keys}
                            />
                        </TwoInOne>
                        <SelectCheckbox
                            lightWithValue
                            label={formatMessage(messages.tonesetSolfa)}
                            name="pitch.solfaNames"
                            value={getFiltersValue(filters, "pitch.solfaNames") as string[]}
                            onChange={change}
                            options={solfaNameOptions(locale, solfaKam).map(({ value, label }) => ({
                                value,
                                label: <span dangerouslySetInnerHTML={{ __html: lengthenSolfa(label, locale, solfaShort) }} />
                            }))}
                            join=" | "
                        />
                        <Checkbox
                            label={formatMessage(messages.tonesetOnly)}
                            name="pitch.solfaNamesOnly"
                            checked={getFiltersValue(filters, "pitch.solfaNamesOnly") as boolean}
                            onChange={change}
                        />
                        <SelectCheckbox
                            lightWithValue
                            label={formatMessage(messages.tonesetAbc)}
                            name="pitch.ABCnames"
                            value={getFiltersValue(filters, "pitch.ABCnames") as string[]}
                            onChange={change}
                            options={ABCOptions(locale)}
                            join=" | "
                        />
                        <Checkbox
                            label={formatMessage(messages.tonesetOnly)}
                            name="pitch.ABCnamesOnly"
                            checked={getFiltersValue(filters, "pitch.ABCnamesOnly") as boolean}
                            onChange={change}
                        />
                        <Select
                            lightWithValue
                            label={formatMessage(messages.scale)}
                            name="pitch.scale"
                            value={getFiltersValue(filters, "pitch.scale") as string}
                            onChange={change}
                            options={scales(formatMessage)}
                        />
                        <div>
                            <Label>
                                <FormattedMessage {...messages.rangeSolfa} />
                            </Label>
                            <TwoInOne>
                                <Select
                                    lightWithValue
                                    minPlaceholder
                                    name="pitch.range.l.i.min"
                                    value={getFiltersValue(filters, "pitch.range.l.i.min") as number}
                                    onChange={change}
                                    options={solfaOptions(locale, solfaShort, solfaKam).map(({ value, label }) => ({
                                        value,
                                        label,
                                    }))}
                                />
                                <div className={"Gap"}>-</div>
                                <Select
                                    lightWithValue
                                    maxPlaceholder
                                    name="pitch.range.h.i.max"
                                    value={getFiltersValue(filters, "pitch.range.h.i.max") as number}
                                    onChange={change}
                                    options={solfaOptions(locale, solfaShort, solfaKam).map(({ value, label }) => ({
                                        value,
                                        label,
                                    }))}
                                />
                            </TwoInOne>
                            <Label>
                                <FormattedMessage {...messages.rangeAbc} />
                            </Label>
                            <TwoInOne>
                                <Select
                                    lightWithValue
                                    minPlaceholder
                                    name="pitch.range.lABC.i.min"
                                    value={getFiltersValue(filters, "pitch.range.lABC.i.min") as number}
                                    onChange={change}
                                    options={ABCOptionsRange(locale)}
                                />
                                <div className={"Gap"}>-</div>
                                <Select
                                    lightWithValue
                                    maxPlaceholder
                                    name="pitch.range.hABC.i.max"
                                    value={getFiltersValue(filters, "pitch.range.hABC.i.max") as number}
                                    onChange={change}
                                    options={ABCOptionsRange(locale)}
                                />
                            </TwoInOne>
                        </div>
                        <Select
                            lightWithValue
                            label={formatMessage(messages.rangeInterval)}
                            name="pitch.range.interval"
                            value={getFiltersValue(filters, "pitch.range.interval") as string}
                            onChange={change}
                            options={intervalOptions(locale)}
                        />
                        <SelectCheckbox
                            lightWithValue
                            label={formatMessage(messages.intervals)}
                            name="pitch.intervals"
                            value={getFiltersValue(filters, "pitch.intervals") as string[]}
                            onChange={change}
                            options={intervalOptions(locale)}
                            join=", "
                        />
                        <Checkbox
                            label={formatMessage(messages.intervalsOnly)}
                            name="pitch.intervalsOnly"
                            checked={getFiltersValue(filters, "pitch.intervalsOnly") as boolean}
                            onChange={change}
                        />
                    </div>
                </Slider>
            )}
        </div>
    );
};

export default Pitch;
